// AdCalculator.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Chip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import PeopleIcon from '@mui/icons-material/People';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import CategoryIcon from '@mui/icons-material/Category';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
import { groupDistricts } from '../../utils/districtAliases';

const AdCalculator = () => {
  const [calcMode, setCalcMode] = useState('pincode'); // 'pincode', 'district', 'taluk' (shown as Constituency)
  const [adType, setAdType] = useState('banner'); // 'banner', 'fullpage'

  // Input states
  const [pincodesInput, setPincodesInput] = useState('');
  const [districtList, setDistrictList] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [talukList, setTalukList] = useState([]);
  const [selectedTaluk, setSelectedTaluk] = useState('');

  // Results state
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const { can } = usePermissions();
  // One entry per real district (Trivandrum / Thiruvananthapuram merged)
  const districtGroups = groupDistricts(districtList);

  // Fetch Districts Dropdown
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/districts`);
        if (res.status === 200 && res.data) {
          const list = Array.isArray(res.data) ? res.data : res.data.districts || [];
          setDistrictList(list);
        }
      } catch (err) {
        console.error('Error fetching districts:', err);
      }
    };
    fetchDistricts();
  }, []);

  // Fetch Taluks when District selected
  useEffect(() => {
    if (!selectedDistrict) {
      setTalukList([]);
      setSelectedTaluk('');
      return;
    }

    const fetchTaluks = async () => {
      try {
        const group = districtGroups.find((g) => g.name === selectedDistrict);
        const ids = group ? group.ids : [];
        if (ids.length === 0) {
          setTalukList([]);
          return;
        }
        // every spelling of the district may hold its own constituency documents
        const lists = await Promise.all(ids.map((id) => axios.get(`${API_BASE_URL}/taluks/district/${id}`)));
        const byName = new Map();
        lists.forEach((res) => {
          const list = Array.isArray(res.data) ? res.data : res.data.taluks || [];
          list.forEach((t) => {
            const key = String(t.talukName || t.name || '').trim().toLowerCase();
            if (key && !byName.has(key)) byName.set(key, t);
          });
        });
        setTalukList([...byName.values()].sort((x, y) => String(x.name).localeCompare(String(y.name))));
      } catch (err) {
        console.error('Error fetching constituencies:', err);
        setTalukList([]);
      }
    };

    fetchTaluks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDistrict, districtList]);

  // Perform Calculation
  const handleCalculate = async () => {
    setError('');
    setResult(null);

    let payload = { adType };
    let endpoint = '';

    if (calcMode === 'pincode') {
      if (!pincodesInput.trim()) {
        setError('Please enter at least one valid 6-digit pincode.');
        return;
      }
      const rawPins = pincodesInput
        .split(',')
        .map((p) => p.trim())
        .filter((p) => /^\d{6}$/.test(p));

      if (rawPins.length === 0) {
        setError('No valid 6-digit pincodes found. Example: 695001, 695002');
        return;
      }

      endpoint = `${API_BASE_URL}/adPriceCalculator/pincode`;
      payload.pincodes = rawPins.map(Number);
    } else if (calcMode === 'district') {
      if (!selectedDistrict) {
        setError('Please select a district.');
        return;
      }
      const group = districtGroups.find((g) => g.name === selectedDistrict);
      if (!group || group.ids.length === 0) {
        setError('Invalid district selected.');
        return;
      }

      endpoint = `${API_BASE_URL}/adPriceCalculator/district`;
      payload.districtIds = group.ids;
    } else if (calcMode === 'taluk') {
      if (!selectedTaluk) {
        setError('Please select a constituency.');
        return;
      }
      const talukObj = talukList.find(
        (t) => (t.talukName || t.name) === selectedTaluk || t._id === selectedTaluk
      );
      const talukId = talukObj ? talukObj._id : selectedTaluk;

      endpoint = `${API_BASE_URL}/adPriceCalculator/taluk`;
      payload.talukIds = [talukId];
    }

    setLoading(true);
    try {
      const response = await axios.post(endpoint, payload);
      if (response.status === 200 && response.data) {
        setResult(response.data);
      } else {
        setError('Failed to calculate ad pricing.');
      }
    } catch (err) {
      console.error('Error calculating ad price:', err);
      const msg = err.response?.data?.message || 'Error executing price calculation.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPincodesInput('');
    setSelectedDistrict('');
    setSelectedTaluk('');
    setResult(null);
    setError('');
  };

  // CSV Export (permission gated)
  const handleExportCSV = () => {
    if (!can('ads.calculator.export')) return;
    if (!result) {
      alert('No calculation result available to export.');
      return;
    }

    const headers = [
      'Ad Type',
      'Calculation Mode',
      'Customers',
      'Shops',
      'Pincodes Covered',
      'Total Target Users',
      'Price Per User (INR)',
      'Total Estimated Cost (INR)'
    ];

    const rows = [
      [
        `"${result.adType.toUpperCase()}"`,
        `"${(calcMode === 'taluk' ? 'constituency' : calcMode).toUpperCase()}"`,
        result.customerCount ?? '',
        result.shopCount ?? '',
        result.pincodeCount ?? '',
        result.totalUsers,
        result.pricing,
        result.totalAmount
      ]
    ];

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `ad_price_calculation_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BackButton />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b', ml: 1 }}>
            Ad Calculator (Web Advertisement Pricing Estimation)
          </Typography>
        </Box>

        {can('ads.calculator.export') && result && (
          <Button variant="contained" color="success" startIcon={<DownloadIcon />} onClick={handleExportCSV}>
            CSV Export
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Input Form Panel */}
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
              Ad Pricing Parameters
            </Typography>

            <Grid container spacing={2.5}>
              {/* Ad Type Selector */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ad Type</InputLabel>
                  <Select value={adType} label="Ad Type" onChange={(e) => setAdType(e.target.value)}>
                    <MenuItem value="banner">Banner Ad (Standard)</MenuItem>
                    <MenuItem value="fullpage">Fullpage Ad (Splash Screen)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Calculation Mode Selector */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Calculation Mode</InputLabel>
                  <Select value={calcMode} label="Calculation Mode" onChange={(e) => setCalcMode(e.target.value)}>
                    <MenuItem value="pincode">By Pincodes</MenuItem>
                    <MenuItem value="district">By District</MenuItem>
                    <MenuItem value="taluk">By Constituency</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Mode Specific Inputs */}
              {calcMode === 'pincode' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Pincodes (Comma-separated)"
                    placeholder="e.g. 695001, 695002, 695003"
                    value={pincodesInput}
                    onChange={(e) => setPincodesInput(e.target.value)}
                    helperText="Enter 6-digit target pincodes separated by commas"
                  />
                </Grid>
              )}

              {calcMode === 'district' && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Select District</InputLabel>
                    <Select
                      value={selectedDistrict}
                      label="Select District"
                      onChange={(e) => setSelectedDistrict(e.target.value)}
                    >
                      {districtGroups.map((g) => (<MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {calcMode === 'taluk' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Select District</InputLabel>
                      <Select
                        value={selectedDistrict}
                        label="Select District"
                        onChange={(e) => setSelectedDistrict(e.target.value)}
                      >
                        {districtGroups.map((g) => (<MenuItem key={g.name} value={g.name}>{g.name}</MenuItem>))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled={!selectedDistrict}>
                      <InputLabel>Select Constituency</InputLabel>
                      <Select
                        value={selectedTaluk}
                        label="Select Constituency"
                        onChange={(e) => setSelectedTaluk(e.target.value)}
                      >
                        {talukList.map((t) => {
                          const tName = t.talukName || t.name;
                          return (
                            <MenuItem key={t._id || tName} value={tName || t._id}>
                              {tName}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              {/* Action Buttons */}
              <Grid item xs={12} sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
                  onClick={handleCalculate}
                  disabled={loading}
                  sx={{ flex: 1, py: 1.2, fontWeight: 700 }}
                >
                  Calculate Cost
                </Button>
                <Button variant="outlined" color="secondary" onClick={handleReset} startIcon={<RefreshIcon />}>
                  Reset
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.06)', minHeight: 340 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
              Calculation Summary
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                <CircularProgress size={48} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Calculating target audience & ad cost...
                </Typography>
              </Box>
            ) : result ? (
              <Box>
                <Card sx={{ backgroundColor: '#0f172a', color: 'white', mb: 3, borderRadius: 2 }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1 }}>
                      Estimated Total Ad Cost
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <CurrencyRupeeIcon sx={{ fontSize: 36, color: '#10b981', mr: 0.5 }} />
                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#10b981' }}>
                        {result.totalAmount.toLocaleString('en-IN')}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, textAlign: 'center' }}>
                      <PeopleIcon color="primary" sx={{ fontSize: 32 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Total Audience Reach
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="#1e293b">
                        {result.totalUsers.toLocaleString('en-IN')} Users
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={6}>
                    <Paper sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, textAlign: 'center' }}>
                      <CategoryIcon color="secondary" sx={{ fontSize: 32 }} />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                        Rate Per User
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="#1e293b">
                        ₹ {result.pricing} / User
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                  {result.customerCount !== undefined
                    ? `${result.customerCount.toLocaleString('en-IN')} customers + ${result.shopCount.toLocaleString('en-IN')} shops across ${result.pincodeCount} pincode(s). `
                    : ''}
                  Total = users × rate per user.
                </Typography>

                <Divider sx={{ my: 2.5 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Ad Format:
                  </Typography>
                  <Chip
                    label={result.adType.toUpperCase()}
                    color="primary"
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </Box>
            ) : (
              <Box sx={{ text: 'center', py: 6, color: '#94a3b8' }}>
                <CalculateIcon sx={{ fontSize: 64, mb: 1, opacity: 0.4 }} />
                <Typography variant="body1" align="center">
                  Select parameters and click <strong>Calculate Cost</strong> to view estimated pricing.
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdCalculator;
